
import { Divider, Form, Input, InputNumber, Select, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { shouldComponentBeVisible } from '../../utils/evaluateCondition';
import globalGetPadding from '../../utils/getPadding';
import { getFieldOptionsByCode } from '../../../../api/customForm';
import { getBorderRadiusValue } from '../../utils/borderRadius';
import { getFormItemLabelNode } from '../../utils/formLabel';
import { getMerchantCode, getMerchantCode2 } from '../../utils/commonFun';

function getFontFamily(fontFamily) {
    switch (fontFamily) {
        case 'MODERN_SANS':
            return '"Helvetica Neue", "Arial Nova", "Nimbus Sans", Arial, sans-serif';
        case 'BOOK_SANS':
            return 'Optima, Candara, "Noto Sans", source-sans-pro, sans-serif';
        case 'ORGANIC_SANS':
            return 'Seravek, "Gill Sans Nova", Ubuntu, Calibri, "DejaVu Sans", source-sans-pro, sans-serif';
        case 'GEOMETRIC_SANS':
            return 'Avenir, "Avenir Next LT Pro", Montserrat, Corbel, "URW Gothic", source-sans-pro, sans-serif';
        case 'HEAVY_SANS':
            return 'Bahnschrift, "DIN Alternate", "Franklin Gothic Medium", "Nimbus Sans Narrow", sans-serif-condensed, sans-serif';
        case 'ROUNDED_SANS':
            return 'ui-rounded, "Hiragino Maru Gothic ProN", Quicksand, Comfortaa, Manjari, "Arial Rounded MT Bold", Calibri, source-sans-pro, sans-serif';
        case 'MODERN_SERIF':
            return 'Charter, "Bitstream Charter", "Sitka Text", Cambria, serif';
        case 'BOOK_SERIF':
            return '"Iowan Old Style", "Palatino Linotype", "URW Palladio L", P052, serif';
        case 'MONOSPACE':
            return '"Nimbus Mono PS", "Courier New", "Cutive Mono", monospace';
        default:
            return undefined;
    }
}

const PhoneWithAreaPropsDefaults = {
    label: '',
    name: '',
    placeholder: '',
    areaOptions: [],
    required: false,
    visible: {
        alwaysShow: true,
        conditions: []
    },
    showPreFix: false,
    isFormItem: true,
    disabled: false
};

const StyledSelect = styled(Select)`
    & {
        display: block !important;
        width: ${({$inputWidth})=>$inputWidth ? $inputWidth+' !important' : '100% !important'};
        max-width: 100% !important;
        min-width: 0 !important;
        overflow: hidden !important;
        box-sizing: border-box !important;
    }
    .ant-select-selector {
        width: ${({$inputWidth})=>($inputWidth+' !important')};
        border-radius: ${({$inputBorderRadius})=> {
            const value = getBorderRadiusValue($inputBorderRadius);
            return value ? `${value} !important` : '';
        }};
        font-size: ${({$inputFontSize})=>($inputFontSize ? $inputFontSize+'px !important' : '')};
        color: ${({$inputColor})=>($inputColor ? $inputColor+' !important' : '')};
        background-color: ${({$inputBackgroundColor})=>($inputBackgroundColor ? $inputBackgroundColor+' !important' : '')};
        border-color: ${({$inputBorderColor})=>($inputBorderColor ? $inputBorderColor+' !important' : '')};
        border-width: ${({$inputBorderWidth})=>($inputBorderWidth ? $inputBorderWidth+'px !important' : '')};
        padding: ${({$inputPadding})=>($inputPadding ? $inputPadding+' !important' : '')};
        
        overflow: hidden;
    }
    .ant-select-selection-item,
    .ant-select-selection-placeholder,
    .ant-select-selection-search-input {
        font-size: ${({$inputFontSize})=>$inputFontSize ? `${$inputFontSize}px !important` : 'inherit !important'};
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 100%;
    }
    .ant-select-selection-wrap {
        min-width: 0;
        overflow: hidden;
    }
    .ant-select-selection-search {
        min-width: 0;
        max-width: 100%;
        overflow: hidden;
    }
`;

export const FormPhoneWithArea = (_a)=> {
    const rootData = _a.form_data.root;
    const root_formStyle = rootData?.data?.formStyle;
    const root_formMessage = rootData?.data?.messageData;
    const isMobile = _a.formClientWidth <= 480;
    var _b, _c, _d, _e, _f;
    var style = _a.style;
    var wStyle = {
        backgroundColor: (_c = style === null || style === void 0 ? void 0 : style.backgroundColor) !== null && _c !== void 0 ? _c : undefined,
        padding: globalGetPadding(style === null || style === void 0 ? void 0 : style.padding, isMobile),
        borderRadius: getBorderRadiusValue((_f = style === null || style === void 0 ? void 0 : style.borderRadius) !== null && _f !== void 0 ? _f : undefined),
    };
    var labelStyle = {
        color: (_b = style === null || style === void 0 ? void 0 : style.color) !== null && _b !== void 0 ? _b : undefined,
        fontSize: (_d = style === null || style === void 0 ? void 0 : style?.fontSize) !== null && _d !== void 0 ? (isMobile ? _d/1.1 : _d) : undefined,
        fontFamily: getFontFamily(style === null || style === void 0 ? void 0 : style.fontFamily),
        fontWeight: (_e = style === null || style === void 0 ? void 0 : style.fontWeight) !== null && _e !== void 0 ? _e : undefined,
    }
    var _m, _n, _o, _p, _q, _r, _s;
    var inputStyle = {
        width: '100%',
        borderRadius: getBorderRadiusValue((_m = style === null || style === void 0 ? void 0 : style.inputBorderRadius) !== null && _m !== void 0 ? _m : undefined),
        fontSize: (_n = style === null || style === void 0 ? void 0 : style?.inputFontSize) !== null && _n !== void 0 ? (isMobile ? _n/1.1 : _n) : undefined,
        color: (_o = style === null || style === void 0 ? void 0 : style.inputColor) !== null && _o !== void 0 ? _o : undefined,
        backgroundColor: (_p = style === null || style === void 0 ? void 0 : style.inputBackgroundColor) !== null && _p !== void 0 ? _p : undefined,
        borderColor: (_q = style === null || style === void 0 ? void 0 : style.inputBorderColor) !== null && _q !== void 0 ? _q : undefined,
        borderWidth: (_r = style === null || style === void 0 ? void 0 : style.inputBorderWidth) !== null && _r !== void 0 ? _r : undefined,
        padding: globalGetPadding(style === null || style === void 0 ? void 0 : style.inputPadding, isMobile),
    }
    // var text = (_g = props === null || props === void 0 ? void 0 : props.text) !== null && _g !== void 0 ? _g : TextPropsDefaults.text;
    // return <div style={wStyle}>
    //     {text}
    // </div>;

    var _g, _h, _i, _j, _k, _l, _t, _u;
    var props = _a.props;
    var label = (_g = props === null || props === void 0 ? void 0 : props.label) !== null && _g !== void 0 ? _g : PhoneWithAreaPropsDefaults.label;
    var name = (_h = props === null || props === void 0 ? void 0 : props.name) !== null && _h !== void 0 ? _h : PhoneWithAreaPropsDefaults.name;
    var placeholder = (_i = props === null || props === void 0 ? void 0 : props.placeholder) !== null && _i !== void 0 ? _i : PhoneWithAreaPropsDefaults.placeholder;
    var required = (_k = props === null || props === void 0 ? void 0 : props.required) !== null && _k !== void 0 ? _k : PhoneWithAreaPropsDefaults.required;
    var visible = (_l = props === null || props === void 0 ? void 0 : props.visible) !== null && _l !== void 0 ? _l : PhoneWithAreaPropsDefaults.visible;
    var formats = (_s = props === null || props === void 0 ? void 0 : props.format) !== null && _s !== void 0 ? _s : PhoneWithAreaPropsDefaults.format;
    var disabled = (_t = props === null || props === void 0 ? void 0 : props.disabled) !== null && _t !== void 0 ? _t : PhoneWithAreaPropsDefaults.disabled;
    var area_options = (_t = props === null || props === void 0 ? void 0 : props.areaOptions) !== null && _t !== void 0 ? _t : PhoneWithAreaPropsDefaults.areaOptions;

    const formStyle = _a.formStyle;
    const item_layout = !formStyle?.layout && root_formStyle?.layout ? root_formStyle?.layout : formStyle?.layout ? 
        formStyle?.layout : '';
    const item_colon = formStyle?.colon === undefined && root_formStyle?.colon !== undefined ? root_formStyle?.colon : formStyle?.colon;

    const requiredText = props?.requiredText !== null && props?.requiredText !== void 0 ? props?.requiredText : root_formMessage?.required;

    const [searchValue, setSearchValue] = useState('');
    const [fieldHidden, setFieldHidden] = useState(false);
    const [fieldLoading, setFieldLoading] = useState(false);
    const [areaOptions, setAreaOptions] = useState([]);
    const isLoading = useMemo(()=>{
        const res = _a?.validateOtpLoading?.find(item=>item.relatedField === name);
        return res?.loading;
    }, [_a?.validateOtpLoading, name])

    const handleOnchange = useCallback((val, isArea)=>{
        if(isArea) {
            const phoneVal = _a?.formRef.getFieldValue(name+"_phone");
            _a.setAllFieldValues && _a.setAllFieldValues(prevData=>({
                ...prevData,
                [name]: {
                    value: val + "-" +phoneVal,
                    isRequired: prevData[name]?.isRequired,
                    type: prevData[name]?.type
                }
            }))
        }
        else {
            const areaVal = _a?.formRef.getFieldValue(name+"_area");
            _a.setAllFieldValues && _a.setAllFieldValues(prevData=>({
                ...prevData,
                [name]: {
                    value: areaVal + "-" + val,
                    isRequired: prevData[name]?.isRequired,
                    type: prevData[name]?.type
                }
            }))
        }
    }, [_a, name])

    function parseValidator(str) {
        const match = str.match(/^\/(.+)\/([gimsuy]*)$/);
        return match ? new RegExp(match[1], match[2]) : new RegExp(str);
    }

    const rulesData = useMemo(()=>{
        let res = required ? [{ required: true, message: requiredText}] : [];
        formats?.forEach(item=>{
            res.push({
                pattern: parseValidator(item.value),
                message: item.message
            })
        })
        return res;
    }, [formats, required, requiredText])
        
    const calVisible = useMemo(()=>{
        if(visible?.alwaysShow) {
            return true;
        }
        else {
            return shouldComponentBeVisible(visible?.conditions, _a?.allFieldValues);
        }
    }, [visible, _a?.allFieldValues])

    useEffect(()=>{
        if(!calVisible && _a?.formRef && !_a?.props?.hiddenOnly) {
            _a?.formRef.setFieldValue(name+"_area", undefined);
            _a?.formRef.setFieldValue(name+"_phone", undefined);
            _a.setAllFieldValues && _a.setAllFieldValues(prevData=>({
                ...prevData,
                [name]: {
                    value: undefined,
                    isRequired: prevData[name]?.isRequired,
                    type: prevData[name]?.type
                }
            }))
        }
    }, [calVisible])

    useEffect(()=>{
        //fieldSource: 通过api获取当前组件的数据，比如option
        if(_a?.fieldSource?.type === 'api' && _a?.fCode) {
            if(fieldLoading) return;
            let dependValues = {};
            if(_a?.fieldSource?.depends?.length) {

            }
            setFieldLoading(true);
            const merchantCode = getMerchantCode(_a?.cmCode, 'form');
            getFieldOptionsByCode({
                "formCode": _a?.fCode,
                "fieldCode": name,
                "dependValues": dependValues
            }, merchantCode || getMerchantCode2(_a?.cmCode), merchantCode ? null : 'form').then(res=>{
                if(res?.options?.length) {
                    const _areaOptions = res.options.filter(item=>!item.disabled);
                    setAreaOptions(_areaOptions);
                }else {
                    setAreaOptions([]);
                }
                setFieldLoading(false);
            }).catch(err=>{
                setFieldLoading(false);
                setAreaOptions(area_options);
            })
        }
        else {
            setAreaOptions(area_options);
        }
    }, [_a?.fieldSource])

    useEffect(()=>{
        //hiddenOnly字段，控制组件的隐藏和只读。hidden时组件渲染但是不显示�?
        if(_a?.props?.hiddenOnly === true) {
            setFieldHidden(true);
        }
        else {
            setFieldHidden(false);
        }
    }, [_a?.props?.hiddenOnly])

    const formItemLabel = getFormItemLabelNode(label, required, labelStyle, item_layout === 'vertical' && item_colon);
    return !calVisible && !fieldHidden ? null : 
        <div
            style={{
                ...wStyle,
                display: 'grid',
                // alignItems: 'flex-start',
                // gridTemplateColumns: 'calc(40% - 8px) calc(60% - 8px)',
                gap: '8px',
                ...fieldHidden ? {
                    visibility: 'hidden',
                    opacity: 0,
                    height: 0,
                    overflow: 'hidden',
                    padding: 0,
                    margin: 0,
                    border: 0
                } : {}
            }}
        >
            <label style={{
                textAlign: (!formStyle?.labelAlign && root_formStyle?.labelAlign === 'right') || formStyle?.labelAlign === 'right' ? 'right' : 'left'
            }}>
                {
                    required ? <span style={{
                        marginInlineEnd: '4px',
                        color: '#ff4d4f',
                        fontSize: '14px',
                        fontFamily: 'SimSun, sans-serif',
                        lineHeight: 1,
                    }}>*</span> : null
                }
                {formItemLabel}
            </label>
            <div style={{
                display: 'grid',
                alignItems: 'flex-start',
                gridTemplateColumns: 'calc(40% - 8px) calc(60% - 8px)',
                gap: '16px',
            }}>
            <Form.Item
                // label={formItemLabel}
                name={name+"_area"}
                className={(!formStyle?.labelAlign && root_formStyle?.labelAlign === 'right') || formStyle?.labelAlign === 'right' ? 
                    'ant-form-item-label-right' : ''}
                layout={item_layout}
                colon={item_colon}
                rules={rulesData}
            >
                <StyledSelect
                    $inputWidth={inputStyle.width}
                    $inputBorderRadius={inputStyle.borderRadius}
                    $inputFontSize={inputStyle.fontSize}
                    $inputColor={inputStyle.color}
                    $inputBackgroundColor={inputStyle.backgroundColor}
                    $inputBorderColor={inputStyle.borderColor}
                    $inputBorderWidth={inputStyle.borderWidth}
                    $inputPadding={inputStyle.padding}
                    placeholder={""}
                    options={searchValue
                        ? areaOptions?.filter(all_sel =>
                            all_sel.label.toLowerCase().includes(searchValue.toLowerCase())
                        )
                        : areaOptions
                    }
                    onChange={(e)=>{handleOnchange(e, true)}}
                    allowClear={true}
                    disabled={disabled}
                    dropdownRender={(menu)=>(<div>
                        <div style={{ padding: 8 }}>
                            <Input
                                placeholder="Search..."
                                value={searchValue}
                                onChange={e => setSearchValue(e.target.value)}
                                onKeyDown={e => e.stopPropagation()}
                                suffix={<SearchOutlined />}
                            />
                        </div>
                        <Divider style={{ margin: '8px 0' }} />
                        {menu}
                    </div>)}
                    // styles={{
                    //     root: inputStyle
                    // }}
                />
            </Form.Item>
            <Form.Item
                name={name+"_phone"}
                // label={label && label.trim() ? " " : undefined}
                className={(!formStyle?.labelAlign && root_formStyle?.labelAlign === 'right') || formStyle?.labelAlign === 'right' ? 
                    'ant-form-item-label-right' : ''}
                layout={item_layout}
                colon={item_colon}
                rules={rulesData}
            >
                <Input
                    placeholder={placeholder} 
                    style={inputStyle}
                    allowClear={true}
                    disabled={disabled}
                    readOnly={isLoading}
                    onChange={(e)=>{handleOnchange(e.target.value)}}
                />
            </Form.Item>
            </div>
        </div>
}




